import React from "react";
import PropTypes from "prop-types";
import { Grid, Box, Typography } from "@mui/material";
import { Newspaper, Star, Calendar } from "lucide-react";
import { colors, customShadows } from "../../../../theme";

/**
 * NewsStatsCards
 * Summary metric cards for the News Management dashboard.
 */
const NewsStatsCards = ({
  totalArticles = 0,
  featuredCount = 0,
  latestPublishedDate = null,
}) => {
  const stats = [
    {
      label: "Total Articles",
      value: totalArticles,
      subtext: "Published announcements",
      icon: <Newspaper size={22} />,
      iconColor: colors.primary.main,
      iconBg: colors.primary.lighter,
      borderColor: colors.dashboard.cardBorder,
    },
    {
      label: "Featured News",
      value: featuredCount,
      subtext: "Pinned on user homepage",
      icon: <Star size={22} />,
      iconColor: colors.warning.main || "#eab308",
      iconBg: colors.warning.lighter || "#fef9c3",
      borderColor: colors.dashboard.cardBorder,
    },
    {
      label: "Latest Update",
      value: latestPublishedDate || "Just Now",
      subtext: "Most recent news release",
      icon: <Calendar size={22} />,
      iconColor: colors.success.main || "#10b981",
      iconBg: colors.success.lighter || "#dcfce7",
      borderColor: colors.dashboard.cardBorder,
      isDate: true,
    },
  ];

  return (
    <Grid container spacing={2.5}  alignItems="stretch">
      {stats.map((stat, idx) => (
        <Grid item xs={12} sm={6} md={4} key={`stat-${idx}`} sx={{ display: "flex" }}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              p: 2.5,
              borderRadius: "16px",
              bgcolor: colors.background.paper,
              border: `1px solid ${stat.borderColor}`,
              boxShadow: customShadows.cardFlat || "0 2px 10px rgba(0,0,0,0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: customShadows.cardHover || "0 8px 24px rgba(0,0,0,0.06)",
              },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1, pr: 1.5, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography
                variant="body2"
                sx={{
                  color: colors.slate[500],
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                variant="h4"
                title={String(stat.value)}
                sx={{
                  fontWeight: 800,
                  color: colors.slate[900],
                  mt: 0.5,
                  fontSize: stat.isDate ? "1.25rem" : "1.75rem",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[400],
                  fontSize: "0.78rem",
                  mt: 0.5,
                  display: "block",
                }}
              >
                {stat.subtext}
              </Typography>
            </Box>

            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                bgcolor: stat.iconBg,
                color: stat.iconColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {stat.icon}
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

NewsStatsCards.propTypes = {
  totalArticles: PropTypes.number,
  featuredCount: PropTypes.number,
  latestPublishedDate: PropTypes.string,
};

export default NewsStatsCards;
